import * as React from "react";
import { useIcon } from "@salt-ds/core";

import { Button } from "../controls/Button";
import { Text } from "../display/Text";
import { useControlled } from "../forms/useControlled";
import "./stepper.css";

/**
 * Stepper and Step, replacing `@salt-ds/core`'s behind `wizard-stepper.tsx`.
 *
 * ── EVERYTHING VISIBLE IS aria-hidden, AND ONE HIDDEN LINE CARRIES THE MEANING ────────
 *
 * This looks backwards and is the most considered part of the component. A step's icon, label
 * and description are three separate elements; read in sequence a screen reader announces
 * "image, Choose plan, pick a tier" as three unrelated fragments, with the STAGE — the thing
 * that actually matters — conveyed only by the icon's shape and colour.
 *
 * So the visible parts are hidden from assistive technology and one visually-hidden line says
 * the whole thing: "Choose plan pick a tier completed". One coherent sentence instead of three
 * fragments and a colour.
 *
 * `aria-current="step"` marks the active one — and the hidden line deliberately omits the state
 * word for that step, because `aria-current` already announces it and saying both reads as
 * "Choose plan active, current step".
 *
 * ── THE STEPPER IS AN <ol> AND A STEP IS AN <li> ──────────────────────────────────────
 *
 * Free from the elements: a screen reader announces "list, 5 items, item 3", which is the
 * position information a wizard needs and which no amount of ARIA on divs reproduces as well.
 */
export type StepStage = "pending" | "active" | "completed" | "inprogress" | "locked";
export type StepStatus = "error" | "warning";
export type StepperOrientation = "horizontal" | "vertical";

const StepperOrientationContext = React.createContext<StepperOrientation>("horizontal");
/** -1 means "no Stepper above me" — see the development warning in Step. */
const StepDepthContext = React.createContext(-1);

export interface StepperProps extends React.OlHTMLAttributes<HTMLOListElement> {
  orientation?: StepperOrientation;
}

export const Stepper = React.forwardRef<HTMLOListElement, StepperProps>(function Stepper(
  { children, className, orientation: orientationProp, ...rest },
  ref,
) {
  const orientationContext = React.useContext(StepperOrientationContext);
  const orientation = orientationProp || orientationContext;
  const depth = React.useContext(StepDepthContext);

  const classes = ["saltStepper", `saltStepper-${orientation}`];
  if (className) classes.push(className);

  return (
    <StepperOrientationContext.Provider value={orientation}>
      {/* Depth increments per nested Stepper, which is what lets a Step know how deep it is
          without being told. */}
      <StepDepthContext.Provider value={depth + 1}>
        <ol className={classes.join(" ")} ref={ref} {...rest}>
          {children}
        </ol>
      </StepDepthContext.Provider>
    </StepperOrientationContext.Provider>
  );
});

function ScreenReaderOnly({
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className="salt-visuallyHidden" {...props}>
      {children}
    </div>
  );
}

function StepConnector() {
  // The line between steps. Purely decorative, and `aria-hidden` so it is not announced as an
  // empty element between every pair.
  return <div aria-hidden className="saltStepConnector" />;
}

function useStepIcon(stage: StepStage, status?: StepStatus) {
  const icons = useIcon();
  /*
   * `status` OUTRANKS `stage`: a step that is active AND in error shows the error. Reversing
   * these makes a failed step look like it is merely in progress, which is the one thing a
   * wizard's status icon exists to prevent.
   */
  const key = {
    error: "ErrorIcon",
    warning: "WarningIcon",
    active: "ActiveIcon",
    completed: "CompletedIcon",
    pending: "PendingIcon",
    inprogress: "InProgressIcon",
    locked: "LockedIcon",
  }[status ?? stage] as keyof typeof icons;
  return icons[key] as React.ComponentType<Record<string, unknown>>;
}

export interface StepProps extends Omit<React.LiHTMLAttributes<HTMLLIElement>, "onToggle"> {
  label?: React.ReactNode;
  description?: React.ReactNode;
  status?: StepStatus;
  stage?: StepStage;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onToggle?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const Step = React.forwardRef<HTMLLIElement, StepProps>(function Step(
  {
    children,
    className,
    defaultExpanded,
    description,
    expanded: expandedProp,
    id: idProp,
    label,
    onToggle,
    stage = "pending",
    status,
    style,
    ...rest
  },
  ref,
) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const depth = React.useContext(StepDepthContext);
  const orientation = React.useContext(StepperOrientationContext);
  const hasNestedSteps = !!children;

  const [expanded, setExpanded] = useControlled<boolean>({
    controlled: expandedProp,
    default: Boolean(defaultExpanded),
  });

  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    // Warnings rather than errors: each of these renders something, just not something correct,
    // and a hard failure in a wizard is worse than a wrong-looking one.
    if (depth === -1) {
      console.warn("<Step /> should be used within a <Stepper /> component!");
    }
    if (depth > 2) {
      console.warn("<Step /> should not be nested more than 2 levels deep!");
    }
    if (orientation === "horizontal" && hasNestedSteps) {
      console.warn("<Stepper /> does not support nested steps in horizontal orientation!");
    }
  }, [depth, orientation, hasNestedSteps]);

  const IconComponent = useStepIcon(stage, status);
  // The top level's icon is larger; nested steps use the base size so they read as subordinate.
  const iconSizeMultiplier = depth === 0 ? 1.5 : 1;
  const stageText = stage === "inprogress" ? "in progress" : stage;
  const state = status || stageText;

  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  const expandTriggerId = `${id}-expand-trigger`;
  const nestedStepperId = `${id}-nested-stepper`;
  const stateId = `${id}-sr-only-state`;
  const substepsId = `${id}-sr-only-substeps`;
  const toggleSubstepsId = `${id}-sr-only-toggle-substeps`;
  // Omitted for the active step: `aria-current="step"` already announces it, and both together
  // read as "Choose plan active, current step".
  const stateText = state !== "active" ? state : "";

  const classes = ["saltStep", `saltStep-stage-${stage}`, `saltStep-depth-${depth}`];
  if (status) classes.push(`saltStep-status-${status}`);
  if (!hasNestedSteps) classes.push("saltStep-terminal");
  if (hasNestedSteps) classes.push(expanded ? "saltStep-expanded" : "saltStep-collapsed");
  if (className) classes.push(className);

  return (
    <li
      aria-current={stage === "active" ? "step" : undefined}
      className={classes.join(" ")}
      id={id}
      style={{ "--saltStep-depth": depth, ...style } as React.CSSProperties}
      {...rest}
      ref={ref}
    >
      {/* The one line a screen reader actually reads for this step. See the note at the top. */}
      <ScreenReaderOnly>
        {`${label} ${description !== undefined ? description : ""} ${stateText}`}
      </ScreenReaderOnly>
      {hasNestedSteps ? (
        <>
          {/* Label fragments for the expand trigger, referenced by id rather than nested inside
              it — so the trigger announces "Choose plan, pick a tier, completed, toggle
              substeps" instead of just "button". */}
          <ScreenReaderOnly aria-hidden id={toggleSubstepsId}>
            toggle substeps
          </ScreenReaderOnly>
          <ScreenReaderOnly aria-hidden id={substepsId}>
            substeps
          </ScreenReaderOnly>
          <ScreenReaderOnly aria-hidden id={stateId}>
            {stateText}
          </ScreenReaderOnly>
        </>
      ) : null}
      <StepConnector />
      <IconComponent aria-hidden className="saltStepIcon" size={iconSizeMultiplier} />
      {/*
        NO `styleAs`, deliberately, and this is a faithful port of something that looks like a
        vendor bug.
        `StepText` destructures `styleAs = "label"` and then never forwards it to `Text` — the
        default is computed and discarded, so every step label renders at BODY size. Passing it
        through, which is what the code plainly intends, makes the label 12px/16px instead of
        14px/20px and every step 4px shorter. Measured exactly that way against the vendor.
        Matching the vendor is the right call here: this is a swap, and shrinking every step
        label in a wizard is a design decision, not a port. Worth revisiting on its own.
      */}
      {label ? (
        <Text aria-hidden className="saltStepText saltStepText-label" id={labelId}>
          {label}
        </Text>
      ) : null}
      {description ? (
        <Text aria-hidden className="saltStepText saltStepText-description" id={descriptionId}>
          {description}
        </Text>
      ) : null}
      {hasNestedSteps && orientation === "vertical" ? (
        <>
          <Button
            appearance="transparent"
            aria-controls={nestedStepperId}
            aria-expanded={expanded}
            aria-labelledby={[labelId, descriptionId, stateId, toggleSubstepsId].join(" ")}
            className="saltStepExpandTrigger"
            id={expandTriggerId}
            onClick={(event) => {
              onToggle?.(event);
              setExpanded(!expanded);
            }}
            sentiment="neutral"
          >
            <ExpandIcon expanded={expanded} />
          </Button>
          <Stepper
            aria-hidden={!expanded}
            aria-labelledby={[labelId, substepsId].join(" ")}
            hidden={!expanded}
            id={nestedStepperId}
          >
            {children}
          </Stepper>
        </>
      ) : null}
    </li>
  );
});

function ExpandIcon({ expanded }: { expanded: boolean }) {
  // Through the semantic icon registry, so an app that swaps its icon set swaps this too.
  const { CollapseIcon, ExpandIcon: Expand } = useIcon();
  const Icon = expanded ? CollapseIcon : Expand;
  return <Icon aria-hidden />;
}
